/**
 * Race-sources unification (spec §3.1) — ONE aggregator over the three disjoint
 * race stores so every surface answers "what races do I have?" identically.
 *
 * The three stores (see the spec's problem table):
 *   - `CustomerEvent` (server, `GET /api/events`) → `EventDTO[]`
 *   - Journey race metadata (`raceYmd`/`raceName`/`customerEventId` on `JourneyView`)
 *   - The anon pending drafts (`localStorage`, `getAnonPendingRaceEvents()`)
 *
 * `collectUpcomingRaces` reads what the caller already has (no fetching beyond
 * the anon draft, which is a synchronous `localStorage` read), normalises each
 * store into an `AthleteRace`, dedupes keeping the highest-fidelity record
 * (event > journey > anon_draft), and returns upcoming races soonest-first.
 *
 * `AthleteRace` is the canonical shape; `WizardRace` is a thin extension of it
 * (adds the wizard's existing fields exactly, plus a `source` passthrough) and
 * lives here so the wizard imports it rather than keeping a parallel copy.
 */

import {
  catalogEventStartToYmd,
  eventDistanceLabel,
  formatEventLocation,
  type EventDTO
} from "./eventsApi";
import { compareYmd } from "./dates";
import { getAnonPendingRaceEvents } from "./anonDraft";
import { journeyRaceName, type JourneyView } from "./journeyApi";

export type RaceSource = "event" | "journey" | "anon_draft";

/**
 * A single upcoming race, normalised across all three stores. `source` records
 * where it came from (and drives provenance labels, spec §3.4); the id fields
 * record how strongly it is anchored: `customerEventId` ⇒ saved to profile,
 * `planGroupId` ⇒ has a plan/journey attached.
 */
export type AthleteRace = {
  name: string;
  /** `YYYY-MM-DD`, local-day semantics matching the injected `today`. */
  dateYmd: string;
  source: RaceSource;
  /** Present ⇢ saved to the profile as a `CustomerEvent`. */
  customerEventId?: string;
  /** Present ⇢ has a plan/journey attached. */
  planGroupId?: string;
  journeyId?: string;
  eventCatalogId?: string;
  distanceLabel?: string;
  location?: string;
};

/**
 * What the wizard applies against — the canonical home for this type (spec
 * §3.1). Mirrors the wizard's historical local `WizardRace` field-for-field,
 * plus a `source` passthrough for provenance labels. Exactly one of
 * `customerEventId` (an existing profile event — reused, never duplicated) /
 * `eventCatalogId` (a catalog link) is typically set; a just-added custom event
 * carries `customerEventId`, an anon custom race carries neither.
 */
export type WizardRace = {
  name: string;
  dateYmd: string;
  /** "City, ST" when known (catalog row or saved event). */
  location?: string;
  eventCatalogId?: string;
  distanceLabel?: string;
  customerEventId?: string;
  source?: RaceSource;
};

/** The weakest identity key — trimmed name + date, or event id when both carry one. */
type RaceIdentity = Pick<AthleteRace, "name" | "dateYmd" | "customerEventId">;

/**
 * Same underlying race — by event id when BOTH carry one, else by trimmed name +
 * exact date. Mirrors the wizard's historical `sameRace` (no fuzzy matching) and
 * is the shared fallback key for both the aggregator's dedupe and the wizard's
 * selected-race exclusion, so the two can never diverge (spec §3.1 step 4).
 */
export function sameRaceIdentity(a: RaceIdentity, b: RaceIdentity): boolean {
  if (a.customerEventId && b.customerEventId) {
    return a.customerEventId === b.customerEventId;
  }
  return a.name.trim() === b.name.trim() && a.dateYmd === b.dateYmd;
}

/** Map an aggregated race to the wizard's shape (one mapper — field mapping can't drift). */
export function toWizardRace(r: AthleteRace): WizardRace {
  return {
    name: r.name,
    dateYmd: r.dateYmd,
    location: r.location,
    eventCatalogId: r.eventCatalogId,
    distanceLabel: r.distanceLabel,
    customerEventId: r.customerEventId,
    source: r.source
  };
}

/** Source fidelity — higher wins a dedupe merge (event > journey > anon_draft). */
const SOURCE_RANK: Record<RaceSource, number> = {
  event: 3,
  journey: 2,
  anon_draft: 1
};

/**
 * Fill gaps on `winner` from `loser` without overwriting anything the winner
 * already has (e.g. a journey's `planGroupId`/`distanceLabel` onto its event).
 */
function mergeInto(winner: AthleteRace, loser: AthleteRace): void {
  winner.customerEventId ??= loser.customerEventId;
  winner.planGroupId ??= loser.planGroupId;
  winner.journeyId ??= loser.journeyId;
  winner.eventCatalogId ??= loser.eventCatalogId;
  winner.distanceLabel ??= loser.distanceLabel;
  winner.location ??= loser.location;
}

/**
 * True when `a` and `b` are the same race by a STRONG key (a key one step above
 * the name+date fallback). Strong keys, any of which matching means "same race":
 *   - `customerEventId` equal
 *   - `planGroupId` equal
 *   - `eventCatalogId` equal AND same date
 * A strong key that is present on both but MISMATCHES does not, on its own, veto
 * a match via a different strong key — but it does prevent falling through to the
 * name+date fallback (spec: "Never dedupe on name+date alone when either side
 * has a stronger key that mismatches").
 */
function strongMatch(a: AthleteRace, b: AthleteRace): boolean {
  if (a.customerEventId && b.customerEventId && a.customerEventId === b.customerEventId) {
    return true;
  }
  if (a.planGroupId && b.planGroupId && a.planGroupId === b.planGroupId) return true;
  if (
    a.eventCatalogId &&
    b.eventCatalogId &&
    a.eventCatalogId === b.eventCatalogId &&
    a.dateYmd === b.dateYmd
  ) {
    return true;
  }
  return false;
}

/** Does either side carry a strong key the other also has, but with a different value? */
function strongKeyConflict(a: AthleteRace, b: AthleteRace): boolean {
  if (a.customerEventId && b.customerEventId && a.customerEventId !== b.customerEventId) {
    return true;
  }
  if (a.planGroupId && b.planGroupId && a.planGroupId !== b.planGroupId) return true;
  if (
    a.eventCatalogId &&
    b.eventCatalogId &&
    (a.eventCatalogId !== b.eventCatalogId || a.dateYmd !== b.dateYmd)
  ) {
    return true;
  }
  return false;
}

/** Should `candidate` collapse onto an already-accepted `existing` record? */
function isSameRace(existing: AthleteRace, candidate: AthleteRace): boolean {
  if (strongMatch(existing, candidate)) return true;
  // A mismatching strong key on either side forbids the weaker name+date merge.
  if (strongKeyConflict(existing, candidate)) return false;
  return sameRaceIdentity(existing, candidate);
}

/**
 * Aggregate every known upcoming race across the three stores into one deduped,
 * soonest-first list. Pure aside from the synchronous anon-draft `localStorage`
 * read; guards against undefined `events`/`journeys`.
 */
export function collectUpcomingRaces(input: {
  events: EventDTO[];
  journeys: JourneyView[];
  today: string;
  isCustomer: boolean;
  /** CustomerEvent ids whose race journey was ARCHIVED ("I stopped this",
   *  journey-focus §3.2) — hide stopped goal leftovers (same rule as calendar
   *  flags). Completed races keep their events; prep-race milestones stay. */
  suppressedGoalEventIds?: readonly string[];
}): AthleteRace[] {
  const { today, isCustomer } = input;
  const events = input.events ?? [];
  const journeys = input.journeys ?? [];
  const suppressed = new Set(input.suppressedGoalEventIds ?? []);

  const raw: AthleteRace[] = [];

  // 1. Saved events (customer OR anon). `GET /api/events` now returns anon-owned
  //    rows too (customer-event-anon-ownable), so this no longer gates on
  //    `isCustomer`; an anon race saved as a real event outranks its localStorage
  //    draft (§4 dedupe), and the draft just fills any gaps.
  for (const e of events) {
    // Stopped-fueling leftovers stay hidden. Tune-ups (milestone) remain even
    // if an old goal journey for that event was archived.
    if (suppressed.has(e.id) && (e.intent ?? "goal") !== "milestone") continue;
    const dateYmd = catalogEventStartToYmd(e.eventDate);
    if (!dateYmd) continue;
    raw.push({
      name: e.name,
      dateYmd,
      source: "event",
      customerEventId: e.id,
      location: formatEventLocation(e) || undefined,
      distanceLabel:
        e.distanceLabel ?? (e.distance ? eventDistanceLabel(e.distance) : undefined)
    });
  }

  // 2. Journey race metadata (customer AND anon — anon build journeys exist).
  //    Mirrors eventCount's filter: non-rolling, dated, not completed.
  for (const j of journeys) {
    if (j.journeyType === "rolling") continue;
    if (!j.raceYmd) continue;
    if (j.status === "completed") continue;
    raw.push({
      name: journeyRaceName(j),
      dateYmd: j.raceYmd,
      source: "journey",
      customerEventId: j.customerEventId ?? undefined,
      planGroupId: j.planGroupId ?? undefined,
      journeyId: j.id,
      distanceLabel: j.planDistance ?? undefined
    });
  }

  // 3. Anon pending drafts (signed-out only) — one candidate per held race
  //    (multi-slot store, spec §6.2).
  if (!isCustomer) {
    for (const draft of getAnonPendingRaceEvents()) {
      raw.push({
        name: draft.name,
        dateYmd: draft.dateYmd,
        source: "anon_draft",
        eventCatalogId: draft.eventCatalogId,
        distanceLabel: draft.distanceLabel,
        planGroupId: draft.planGroupId,
        journeyId: draft.journeyId
      });
    }
  }

  // 4. Dedupe, keeping the highest-fidelity record and filling its gaps from the
  //    loser. Process highest-fidelity sources first so the winner is stable.
  const ordered = [...raw].sort((a, b) => SOURCE_RANK[b.source] - SOURCE_RANK[a.source]);
  const merged: AthleteRace[] = [];
  for (const candidate of ordered) {
    const existing = merged.find((m) => isSameRace(m, candidate));
    if (existing) {
      mergeInto(existing, candidate);
    } else {
      merged.push({ ...candidate });
    }
  }

  // 5. Upcoming only, soonest first.
  return merged
    .filter((r) => compareYmd(r.dateYmd, today) >= 0)
    .sort((a, b) => compareYmd(a.dateYmd, b.dateYmd));
}

/**
 * Goal races saved as `CustomerEvent` rows with no linked *active* FuelJourney —
 * and that never had a stopped/archived race journey either. Athlete added the
 * race but didn't finish the plan wizard.
 */
export function collectUnfinishedGoalEvents(input: {
  events: EventDTO[];
  journeys: JourneyView[];
  today: string;
  /** CustomerEvent ids whose race journey was ARCHIVED (§3.2) — exclude. */
  suppressedGoalEventIds?: readonly string[];
}): AthleteRace[] {
  const journeyEventIds = new Set(
    input.journeys
      .filter((j) => j.journeyType !== "rolling" && j.customerEventId)
      .map((j) => j.customerEventId!)
  );
  const suppressed = new Set(input.suppressedGoalEventIds ?? []);
  const out: AthleteRace[] = [];
  for (const e of input.events ?? []) {
    if ((e.intent ?? "goal") !== "goal") continue;
    const dateYmd = catalogEventStartToYmd(e.eventDate);
    if (!dateYmd || compareYmd(dateYmd, input.today) < 0) continue;
    if (journeyEventIds.has(e.id)) continue;
    if (suppressed.has(e.id)) continue;
    out.push({
      name: e.name,
      dateYmd,
      source: "event",
      customerEventId: e.id,
      eventCatalogId: e.eventCatalogId ?? undefined,
      distanceLabel:
        e.distanceLabel ?? (e.distance ? eventDistanceLabel(e.distance) : undefined),
      location: formatEventLocation(e) || undefined
    });
  }
  return out.sort((a, b) => compareYmd(a.dateYmd, b.dateYmd));
}
